import { useState } from "react";
import { rewrite, type RewriteMode } from "../lib/ai";

/** Trim the dated suffix so "claude-haiku-4-5-20251001" reads as "claude-haiku-4-5". */
const shortModel = (m: string) => m.replace(/-\d{8}$/, "");

/**
 * "Rewrite" button + suggestion preview. Never overwrites silently — the user
 * sees before/after and explicitly accepts. Drop it next to any text input:
 *
 *   <AiRewrite text={title} onAccept={setTitle} />
 */
export function AiRewrite({
  text,
  onAccept,
  compact = false,
  defaultMode = "polish",
  affix = false,
}: {
  text: string;
  onAccept: (next: string) => void;
  compact?: boolean;
  /** Quick-add uses a mode that preserves its shorthand tokens. */
  defaultMode?: RewriteMode;
  /** Render the trigger *inside* the text field (right edge) and flow the panel
   *  below it, pushing siblings down. Parent must wrap input + this in
   *  `<div className="airw-field">`. Used for the task modal and note title. */
  affix?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<RewriteMode>(defaultMode);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [meta, setMeta] = useState("");
  const [err, setErr] = useState<string | null>(null);

  async function run(m: RewriteMode) {
    setMode(m);
    setBusy(true);
    setErr(null);
    const started = performance.now();
    try {
      const res = await rewrite(text, m);
      const secs = ((performance.now() - started) / 1000).toFixed(1);
      setSuggestion(res.suggestion);
      setMeta(`${shortModel(res.model)} · ${secs}s`);
    } catch (e) {
      setErr((e as Error).message);
      setSuggestion(null);
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setSuggestion(null);
    setErr(null);
  }

  // The two mode pills. The quick-add bar needs shorthand-stripping variants
  // (plain "polish"/"shorten" would leave the #project/p1/~30m codes in place),
  // so it gets the "quickadd" pair; everywhere else uses the general pair.
  const [polishMode, shortenMode]: [RewriteMode, RewriteMode] =
    defaultMode === "quickadd" ? ["quickadd", "quickadd-short"] : ["polish", "shorten"];

  return (
    // Anchors the panel so it overlays rather than stretching a narrow container
    // (the jots box is a ~250px sidebar column — inline, the panel blew off-screen).
    // `affix` opts out: the button sits inside the field and the panel pushes
    // form fields down (via display:contents on the wrapper) instead of hiding them.
    <span className={`airw${affix ? " airw-affix" : ""}`}>
      <button
        type="button"
        className={`airw-btn${compact ? " compact" : ""}`}
        disabled={!text.trim() || busy}
        onClick={() => run(defaultMode)}
        title={text.trim() ? "Rewrite with AI" : "Type something first"}
      >
        <span className="airw-ico">✦</span>
        {compact ? "" : busy && !suggestion ? " Rewriting…" : " Rewrite"}
      </button>

      {(suggestion || err) && (
        <div className="airw-panel">
          {err ? (
            <div className="airw-err">{err}</div>
          ) : (
            <>
              <div className="airw-head">
                <span>
                  <span className="airw-ico">✦</span> Suggestion
                </span>
                <span className="airw-meta">{meta}</span>
              </div>
              <div className="airw-label">BEFORE</div>
              <div className="airw-before">{text}</div>
              <div className="airw-label after">AFTER</div>
              <div className="airw-after">{suggestion}</div>
            </>
          )}

          <div className="airw-actions">
            {!err && (
              <button
                type="button"
                className="airw-accept"
                onClick={() => {
                  if (suggestion) onAccept(suggestion);
                  close();
                }}
              >
                Accept
              </button>
            )}
            <button type="button" className="airw-secondary" disabled={busy} onClick={() => run(mode)}>
              ↻ {busy ? "…" : "Try again"}
            </button>
            <button type="button" className="airw-plain" onClick={close}>
              Cancel
            </button>
            {!err && (
              <span className="airw-modes">
                <button
                  type="button"
                  className={`airw-pill${mode === polishMode ? " on" : ""}`}
                  disabled={busy}
                  onClick={() => run(polishMode)}
                >
                  Polish
                </button>
                <button
                  type="button"
                  className={`airw-pill alt${mode === shortenMode ? " on" : ""}`}
                  disabled={busy}
                  onClick={() => run(shortenMode)}
                >
                  Shorten
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
